import { useMemo, useState, useEffect, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Camera } from "lucide-react";
import { UserProfile } from "@/types/profile";
import { ManageAccess, AvailablePermission } from "@/components/ManageAccess";
import { profileApi } from "@/api/profile";
import { toast } from "sonner";
import {
    generatePermissionOptions,
    createBusinessNameMapping,
    createTeamNameMapping,
    getBasePermission,
    type Business,
    type Team,
} from "@/utils/permissions";
import { AVAILABLE_SERVICES } from "@/utils/constants";

interface ProfileViewProps {
    profile: UserProfile;
    userName: string | null;
    profileImage: string;
    isEditing: boolean;
    onImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onLogout: () => void;
    onProfileUpdate?: (updatedProfile: UserProfile) => void;
    userId?: string | null;
    autoOpenManageAccess?: boolean;
}

export function ProfileView({
    profile,
    userName,
    profileImage,
    isEditing,
    onImageUpload,
    onLogout,
    onProfileUpdate,
    userId,
    autoOpenManageAccess = false,
}: ProfileViewProps) {
    const [currentProfile, setCurrentProfile] = useState<UserProfile>(profile);
    const [businessList] = useState<Business[]>([]);
    const [teamList] = useState<Team[]>([]);

    useEffect(() => {
        setCurrentProfile(profile);
    }, [profile]);

    const userInitials = useMemo(() => {
        return (userName || "User")
            .split(" ")
            .map((n) => n[0])
            .join("")
            .substring(0, 2)
            .toUpperCase();
    }, [userName]);

    // Generate available permissions from business and team lists
    const availablePermissions = useMemo<AvailablePermission[]>(() => {
        if (businessList.length === 0 || teamList.length === 0) {
            return [];
        }

        const options = generatePermissionOptions(AVAILABLE_SERVICES, businessList, teamList);

        // Filter out permissions that user already has
        const existingPermissionBases = new Set(
            (currentProfile.permissions || []).map((perm) => {
                if (perm === "*.*.*.*") return "*.*.*";
                return getBasePermission(perm);
            })
        );

        return options
            .filter((option) => {
                const basePermission = getBasePermission(option.value);
                return !existingPermissionBases.has(basePermission);
            })
            .map((option) => ({
                value: option.value,
                label: option.label,
                business: option.business,
                product: option.product,
                service: option.service,
            }));
    }, [businessList, teamList, currentProfile.permissions]);

    // Create name mappings for display
    const businessNameMapping = useMemo(
        () => createBusinessNameMapping(businessList),
        [businessList]
    );
    const teamNameMapping = useMemo(
        () => createTeamNameMapping(teamList),
        [teamList]
    );

    // Handle request service
    const handleRequestService = useCallback(
        async (permission: AvailablePermission) => {
            try {
                const parts = permission.value.split(".");
                const business = parts[0] === "*" || parts[0] === "All Business" ? "*" : parts[0];
                const team = parts[1] === "*" || parts[1] === "All Team" ? "*" : parts[1];
                const service = parts[2] || "*";
                const level = parts[3] || "r";
                const serviceWithLevel = `${service}.${level}`; // Format: "cms.r"
                const userId = currentProfile.email || userName || "";

                await profileApi.requestService([{ business, team, service: serviceWithLevel, id: userId }]);
                toast.success("Service request submitted successfully");
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : "Failed to request service";
                toast.error(errorMessage);
                throw error;
            }
        },
        [currentProfile, userName]
    );

    const handleCancelRequest = useCallback(
        async (requestId: string) => {
            try {
                // Wait briefly for the server to persist the cancellation before refetching
                await new Promise(resolve => setTimeout(resolve, 1000));

                const { profile: updatedProfile } = await profileApi.getUserData({
                    userName,
                    userEmail: currentProfile.email,
                });

                setCurrentProfile(updatedProfile);

                if (onProfileUpdate) {
                    onProfileUpdate(updatedProfile);
                }
            } catch (error) {
                console.error("Failed to refresh profile after canceling request:", error);
            }
        },
        [userName, currentProfile.email, onProfileUpdate]
    );

    const handleDone = useCallback(
        async () => {
            try {
                // Wait briefly for the server to persist the new request before refetching
                await new Promise(resolve => setTimeout(resolve, 1000));

                const { profile: updatedProfile } = await profileApi.getUserData({
                    userName,
                    userEmail: currentProfile.email,
                });

                setCurrentProfile(updatedProfile);

                if (onProfileUpdate) {
                    onProfileUpdate(updatedProfile);
                }
            } catch (error) {
                console.error("Failed to refresh profile after requesting services:", error);
            }
        },
        [userName, currentProfile.email, onProfileUpdate]
    );

    return (
        <div className=" animate-fade-in max-w-[1200px] space-y-8">
            
            <Card className="glass-card overflow-hidden">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 sm:p-6 border-b">
                    <h3 className="text-base sm:text-lg font-semibold">Profile Information</h3>
                    <Button
                        variant="destructive"
                        size="sm"
                        className=" w-full sm:w-auto"
                        onClick={onLogout}
                    >
                        LOGOUT
                    </Button>
                </div>
                <div className="p-4 sm:p-6 lg:p-8">
                    <div className="flex flex-col md:flex-row gap-6 sm:gap-8 lg:gap-10 items-center md:items-start">
                        {/* Left: Avatar */}
                        <div className="flex flex-col items-center text-center space-y-3">
                            <div className="relative group">
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    id="profile-image-upload"
                                    onChange={onImageUpload}
                                    disabled={!isEditing}
                                />
                                <label
                                    htmlFor="profile-image-upload"
                                    className={`block relative ${isEditing ? "cursor-pointer" : "cursor-default"}`}
                                >
                                    <Avatar className="w-24 h-24 sm:w-32 sm:h-32 border-4 border-background shadow-xl bg-muted">
                                        <AvatarImage src={profileImage} />
                                        <AvatarFallback className="gradient-primary text-primary-foreground text-3xl sm:text-4xl font-bold">
                                            {userInitials}
                                        </AvatarFallback>
                                    </Avatar>
                                    {isEditing && (
                                        <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Camera className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                                        </div>
                                    )}
                                </label>
                            </div>
                            <div>
                                <h2 className="text-lg sm:text-xl font-bold uppercase">{profile.name}</h2>
                                <p className="text-sm text-muted-foreground">{profile.role || "N/A"}</p>
                            </div>
                        </div>

                        {/* Right: Details Grid */}
                        <div className="flex-1 w-full grid grid-cols-1 sm:grid-cols-2 gap-x-6 sm:gap-x-8 lg:gap-x-12 gap-y-6 sm:gap-y-8">
                            <div className="space-y-1">
                                <p className="text-xs sm:text-sm font-medium text-muted-foreground">Primary Email:</p>
                                <p className="text-sm sm:text-base font-medium break-all">{profile.email}</p>
                            </div>
                            <div className="space-y-1">
                                <p className="text-xs sm:text-sm font-medium text-muted-foreground">Secondary Email:</p>
                                <p className="text-sm sm:text-base font-medium break-all">{profile.secondaryEmail || "N/A"}</p>
                            </div>
                            <div className="space-y-1">
                                <p className="text-xs sm:text-sm font-medium text-muted-foreground">Business:</p>
                                <p className="text-sm sm:text-base font-medium">{profile.business || "Languages"}</p>
                            </div>
                            <div className="space-y-1">
                                <p className="text-xs sm:text-sm font-medium text-muted-foreground">Team:</p>
                                <p className="text-sm sm:text-base font-medium">{profile.team || "Maharashtra Times"}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Manage Access Card */}
            <ManageAccess
                permissions={currentProfile.permissions || []}
                decoded_permissions={currentProfile.decoded_permissions}
                user_req={currentProfile.user_req}
                availablePermissions={availablePermissions}
                onRequestPermission={handleRequestService}
                onCancelRequest={handleCancelRequest}
                onDone={handleDone}
                isProfile={true}
                userId={userId || currentProfile.id || null}
                businessNameMapping={businessNameMapping}
                teamNameMapping={teamNameMapping}
                autoOpenDialog={autoOpenManageAccess}
                defaultTeam={currentProfile?.team || ""}
            />

        </div>
    );
}
